import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard, Eye, BookOpen, LogOut,
  ChevronRight, Trash2, UserX, UserCheck, RefreshCw, Search,
  TrendingUp, DollarSign, ArrowLeft, MessageSquare,
  Send, Loader2, BarChart3, Globe, Monitor, Smartphone, Mail,
  Download, Clock, MapPin, Menu, X, Zap,
  ExternalLink, Copy, Check, ArrowUpRight, Activity, Circle,
  Bell, BellRing, Megaphone, Sparkles, PenLine, Eye as EyeIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const CHAT_API = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;
const BROADCAST_API = `${process.env.REACT_APP_BACKEND_URL}/api/broadcast`;
const GREEN = '#22c55e';

// ─── NOTIFICATION SOUND ───
function playNotifSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    // Two-tone chime
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, now + i * 0.15);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.4);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (e) { /* silent fail */ }
}

// ─── PUSH NOTIFICATION HELPERS ───
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function registerPushSubscription(token) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    const res = await fetch(`${CHAT_API}/vapid-public-key`);
    const { public_key } = await res.json();
    if (!public_key) return null;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
    }
    const subJson = sub.toJSON();
    await fetch(`${CHAT_API}/push-subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ endpoint: subJson.endpoint, keys: subJson.keys }),
    });
    return sub;
  } catch (e) {
    console.warn('Push registration failed:', e);
    return null;
  }
}

const fetchAdmin = async (path, token, options = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
  });
  if (res.status === 401 || res.status === 403) throw new Error('unauthorized');
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

// ─── LOGIN ───
const AdminLoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      if (!res.ok) { setError('Identifiants incorrects'); return; }
      const data = await res.json();
      localStorage.setItem('admin_token', data.access_token);
      onLogin(data.access_token);
    } catch { setError('Erreur de connexion'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#080808' }}>
      <form onSubmit={handleSubmit} className="w-full max-w-[400px] mx-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GREEN }} />
            <span className="text-zinc-500 text-[13px] tracking-wider uppercase" style={{ fontFamily: '"Inter", sans-serif' }}>Admin Panel</span>
          </div>
          <h1 className="text-[32px] font-bold text-white mb-2" style={{ fontFamily: '"Inter", sans-serif' }}>OpenRoom</h1>
          <p className="text-zinc-600 text-[14px]">Connecte-toi pour gerer ta plateforme</p>
        </div>
        {error && <div data-testid="admin-login-error" className="px-4 py-3 rounded-xl mb-5 text-[13px] text-red-400" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)' }}>{error}</div>}
        <div className="space-y-3 mb-5">
          <input data-testid="admin-email-input" type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
            className="w-full px-5 py-3.5 rounded-xl text-[14px] text-white placeholder:text-zinc-600 focus:outline-none transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', fontFamily: '"Inter", sans-serif' }} />
          <input data-testid="admin-password-input" type="password" placeholder="Mot de passe" value={password} onChange={e => setPassword(e.target.value)} required
            className="w-full px-5 py-3.5 rounded-xl text-[14px] text-white placeholder:text-zinc-600 focus:outline-none transition-all"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', fontFamily: '"Inter", sans-serif' }} />
        </div>
        <button data-testid="admin-login-submit" type="submit" disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-[14px] text-black transition-all disabled:opacity-40 hover:brightness-110"
          style={{ backgroundColor: GREEN, fontFamily: '"Inter", sans-serif' }}>
          <Circle size={6} fill="black" />
          {loading ? 'Connexion...' : 'Se connecter'}
          <ChevronRight size={16} />
        </button>
      </form>
    </div>
  );
};

// ─── SHARED ───
const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between mb-8">
    <div>
      <h1 className="text-[28px] font-bold text-white tracking-tight" style={{ fontFamily: '"Inter", sans-serif' }}>{title}</h1>
      {subtitle && <p className="text-zinc-500 text-[14px] mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

const Pill = ({ active, onClick, children }) => (
  <button onClick={onClick}
    className={`px-4 py-2 rounded-full text-[12px] font-medium transition-all ${active ? 'text-black' : 'text-zinc-500 hover:text-zinc-300'}`}
    style={{ backgroundColor: active ? GREEN : 'rgba(255,255,255,0.04)', border: `1px solid ${active ? GREEN : 'rgba(255,255,255,0.06)'}` }}>
    {children}
  </button>
);

const RefreshBtn = ({ onClick }) => (
  <button onClick={onClick} className="p-2.5 rounded-xl text-zinc-600 hover:text-white hover:bg-white/5 transition-all"><RefreshCw size={15} /></button>
);

// ─── DASHBOARD ───
const AdminDashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [recap, setRecap] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetchAdmin('/stats', token).then(setStats).catch(() => {}),
      fetchAdmin('/daily-recap', token).then(setRecap).catch(() => {}),
      fetchAdmin('/funnel?days=30', token).then(setFunnel).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-zinc-700" size={24} /></div>;
  if (!stats) return <p className="text-red-400">Erreur de chargement</p>;

  const maxVisits = Math.max(...(recap?.days || []).map(d => d.visits), 1);

  return (
    <div>
      {/* Header */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN }} />
          <span className="text-zinc-500 text-[11px] tracking-wider uppercase">Tableau de bord</span>
        </div>
        <h1 className="text-[36px] font-bold text-white tracking-tight leading-tight" style={{ fontFamily: '"Inter", sans-serif' }}>
          Bienvenue, <span style={{ color: GREEN }}>Boss</span>
        </h1>
        <p className="text-zinc-500 text-[15px] mt-2">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[
          { label: 'Visiteurs (24h)', value: stats.recent_visits_24h, icon: Eye, trend: '+12%' },
          { label: 'Total leads', value: recap?.total_leads || 0, icon: Mail, trend: `+${recap?.days?.slice(-1)[0]?.leads || 0} auj.` },
          { label: 'Paiements', value: stats.paid_payments, icon: CreditCard },
          { label: 'Revenu', value: `${stats.total_revenue.toFixed(0)}€`, icon: DollarSign },
        ].map((kpi, i) => (
          <div key={i} className="group rounded-2xl p-6 transition-all hover:translate-y-[-2px]"
            style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center justify-between mb-4">
              <kpi.icon size={18} className="text-zinc-600" />
              {kpi.trend && (
                <span className="flex items-center gap-0.5 text-[11px] font-medium" style={{ color: GREEN }}>
                  <ArrowUpRight size={11} />{kpi.trend}
                </span>
              )}
            </div>
            <p className="text-[30px] font-bold text-white tracking-tight" style={{ fontFamily: '"Inter", sans-serif' }}>{kpi.value}</p>
            <p className="text-zinc-600 text-[12px] mt-1 tracking-wide">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="flex items-center gap-6 mb-10 px-6 py-5 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
        {[
          { label: 'Membres', value: stats.total_users, icon: Users },
          { label: 'Checkouts (24h)', value: stats.recent_checkouts_24h, icon: TrendingUp },
          { label: 'Transactions', value: stats.total_payments, icon: Activity },
        ].map((s, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div className="w-px h-8" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />}
            <div className="flex items-center gap-3">
              <s.icon size={15} className="text-zinc-600" />
              <div>
                <p className="text-white text-[16px] font-bold">{s.value}</p>
                <p className="text-zinc-600 text-[11px]">{s.label}</p>
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Funnel Conversion */}
      {funnel && funnel.totals && (
        <div className="mb-10 rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-2.5">
              <TrendingUp size={15} style={{ color: GREEN }} />
              <span className="text-white text-[14px] font-semibold">Funnel de conversion</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-zinc-700 text-[11px]">30 derniers jours</span>
              <button data-testid="funnel-csv-export" onClick={() => {
                const rows = [['Date', 'Visites', 'Leads', 'Checkouts', 'Paiements']];
                (funnel.daily || []).forEach(d => rows.push([d.date, d.visits, d.leads, d.checkouts, d.payments]));
                rows.push([]);
                rows.push(['Total', funnel.totals.visits, funnel.totals.leads, funnel.totals.checkouts, funnel.totals.payments]);
                rows.push(['Taux conversion', '', `${funnel.rates.visit_to_lead}%`, `${funnel.rates.lead_to_checkout}%`, `${funnel.rates.checkout_to_payment}%`]);
                rows.push(['Taux global', '', '', '', `${funnel.rates.overall}%`]);
                const csv = rows.map(r => r.join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = `funnel_openroom_${new Date().toISOString().slice(0,10)}.csv`;
                a.click(); URL.revokeObjectURL(url);
              }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all hover:bg-white/5"
                style={{ color: GREEN, border: '1px solid rgba(34,197,94,0.2)' }}>
                <Download size={12} /> CSV
              </button>
            </div>
          </div>
          <div className="px-6 py-6">
            <div className="flex items-center gap-3">
              {[
                { label: 'Visites', value: funnel.totals.visits, color: '#6b7280' },
                { label: 'Leads', value: funnel.totals.leads, rate: funnel.rates.visit_to_lead, color: '#3b82f6' },
                { label: 'Checkouts', value: funnel.totals.checkouts, rate: funnel.rates.lead_to_checkout, color: '#f59e0b' },
                { label: 'Payes', value: funnel.totals.payments, rate: funnel.rates.checkout_to_payment, color: GREEN },
              ].map((step, i, arr) => (
                <React.Fragment key={i}>
                  <div className="flex-1 text-center rounded-xl py-4 px-2" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <p className="text-[24px] font-bold text-white" style={{ fontFamily: '"Inter", sans-serif' }}>{step.value}</p>
                    <p className="text-[11px] mt-1 tracking-wide" style={{ color: step.color }}>{step.label}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                      <ChevronRight size={14} className="text-zinc-700" />
                      {step.rate !== undefined && (
                        <span className="text-[10px] font-semibold" style={{ color: step.rate > 0 ? GREEN : '#4b5563' }}>{step.rate}%</span>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            {funnel.rates.overall > 0 && (
              <div className="mt-4 pt-4 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <span className="text-zinc-600 text-[12px]">Taux global visite &rarr; paiement</span>
                <span className="text-[13px] font-bold" style={{ color: GREEN }}>{funnel.rates.overall}%</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Activity Chart + Table */}
      {recap?.days && (
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-2.5">
              <BarChart3 size={15} style={{ color: GREEN }} />
              <span className="text-white text-[14px] font-semibold">Activite quotidienne</span>
            </div>
            <span className="text-zinc-700 text-[11px]">14 derniers jours</span>
          </div>

          {/* Mini bar chart */}
          <div className="px-6 py-5 flex items-end gap-[6px]" style={{ height: 120, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {recap.days.map((day, i) => {
              const h = Math.max(4, (day.visits / maxVisits) * 70);
              const isToday = i === recap.days.length - 1;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-sm transition-all" style={{ height: h, backgroundColor: isToday ? GREEN : 'rgba(255,255,255,0.08)', minWidth: 4 }} />
                  <span className="text-[9px] text-zinc-700">{day.date}</span>
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {['Date', 'Visites', 'Uniques', 'Leads', 'Checkouts', 'Payes'].map(h => (
                    <th key={h} className={`${h === 'Date' ? 'text-left px-6' : 'text-center px-3'} py-3 text-zinc-600 font-medium text-[10px] uppercase tracking-[0.12em]`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...recap.days].reverse().map((day, i) => (
                  <tr key={i} className="hover:bg-white/[0.01] transition-colors" style={{ borderBottom: '1px solid rgba(255,255,255,0.025)' }}>
                    <td className="px-6 py-3.5 text-zinc-400 font-medium">{day.date_full}</td>
                    <td className="text-center px-3 py-3.5"><span className={day.visits > 0 ? 'text-zinc-300' : 'text-zinc-800'}>{day.visits}</span></td>
                    <td className="text-center px-3 py-3.5"><span className={day.unique_visitors > 0 ? 'text-zinc-300' : 'text-zinc-800'}>{day.unique_visitors}</span></td>
                    <td className="text-center px-3 py-3.5"><span className={day.leads > 0 ? 'font-semibold' : 'text-zinc-800'} style={{ color: day.leads > 0 ? GREEN : undefined }}>{day.leads}</span></td>
                    <td className="text-center px-3 py-3.5"><span className={day.checkouts > 0 ? 'text-amber-400 font-semibold' : 'text-zinc-800'}>{day.checkouts}</span></td>
                    <td className="text-center px-3 py-3.5"><span className={day.payments > 0 ? 'font-semibold' : 'text-zinc-800'} style={{ color: day.payments > 0 ? GREEN : undefined }}>{day.payments}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── VISITORS ───
const AdminVisitors = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchAdmin(`/stats/visitors?period=${period}`, token).then(setStats).catch(() => {}).finally(() => setLoading(false));
  }, [token, period]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Visiteurs" subtitle="Analyse du trafic" actions={<RefreshBtn onClick={load} />} />
      <div className="flex gap-2 mb-6">
        {[{ id: 'today', l: "Aujourd'hui" }, { id: '7d', l: '7 jours' }, { id: '30d', l: '30 jours' }, { id: 'all', l: 'Tout' }].map(p => (
          <Pill key={p.id} active={period === p.id} onClick={() => setPeriod(p.id)}>{p.l}</Pill>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-700" size={24} /></div> : stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { l: 'Pages vues', v: stats.total_views || 0, icon: Eye },
              { l: 'Uniques', v: stats.unique_visitors || 0, icon: Users },
              { l: 'Checkouts', v: stats.total_checkouts || 0, icon: TrendingUp },
              { l: 'Paiements', v: stats.total_payments || 0, icon: CreditCard },
            ].map((k, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <k.icon size={15} className="text-zinc-600 mb-3" />
                <p className="text-[24px] font-bold text-white">{k.v}</p>
                <p className="text-zinc-600 text-[11px] mt-1">{k.l}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[
              { title: 'Pages populaires', icon: Globe, data: stats.top_pages, keyField: 'page' },
              { title: 'Pays', icon: MapPin, data: stats.top_countries, keyField: 'country' },
              { title: 'Appareils', icon: Monitor, data: stats.top_devices, keyField: 'device' },
            ].map((section, si) => (
              <div key={si} className="rounded-2xl p-5" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h3 className="text-white text-[13px] font-semibold mb-4 flex items-center gap-2">
                  <section.icon size={14} className="text-zinc-500" />{section.title}
                </h3>
                {(section.data || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: i < (section.data?.length || 0) - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                    <span className="text-zinc-400 text-[12px] truncate flex-1">{item[section.keyField] || item._id || 'Inconnu'}</span>
                    <span className="text-zinc-300 text-[12px] font-semibold ml-2">{item.count}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CHAT ───
const AdminChat = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [info, setInfo] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const loadSessions = useCallback(() => {
    setLoading(true);
    fetchAdmin('/chat/sessions', token).then(d => setSessions(d.sessions || [])).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const open = (id) => {
    setSel(id);
    fetchAdmin(`/chat/messages/${id}`, token).then(d => { setMessages(d.messages || []); setInfo(d.session || null); }).catch(() => {});
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => { fetchAdmin(`/chat/messages/${id}`, token).then(d => setMessages(d.messages || [])).catch(() => {}); }, 5000);
  };

  const sendReply = async () => {
    if (!reply.trim() || sending) return; setSending(true);
    try {
      await fetchAdmin('/chat/reply', token, { method: 'POST', body: JSON.stringify({ session_id: sel, message: reply }) });
      setReply('');
      const d = await fetchAdmin(`/chat/messages/${sel}`, token); setMessages(d.messages || []);
    } catch {} finally { setSending(false); }
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  if (!sel) {
    return (
      <div>
        <PageHeader title="Messages" subtitle={`${sessions.length} conversation${sessions.length !== 1 ? 's' : ''}`} actions={<RefreshBtn onClick={loadSessions} />} />
        {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-700" size={24} /></div> : (
          <div className="space-y-2" data-testid="admin-chat-sessions">
            {sessions.map(s => (
              <button key={s.session_id} onClick={() => open(s.session_id)} data-testid={`chat-session-${s.session_id}`}
                className="w-full flex items-center gap-4 p-5 rounded-2xl text-left transition-all hover:bg-white/[0.02]"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: `1px solid ${s.unread ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.05)'}` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: s.unread ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.05)' }}>
                  <MessageSquare size={16} className={s.unread ? 'text-green-400' : 'text-zinc-500'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-[14px] font-semibold truncate">{s.visitor_name || s.visitor_email || `Visiteur #${s.session_id}`}</p>
                    {s.unread && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: GREEN }} />}
                  </div>
                  <p className="text-zinc-600 text-[12px] truncate mt-0.5">{s.last_message?.message || '—'}</p>
                </div>
                <span className="text-zinc-700 text-[11px] flex-shrink-0">{s.message_count} msg</span>
              </button>
            ))}
            {sessions.length === 0 && <div className="text-center py-20"><MessageSquare size={32} className="mx-auto mb-3 text-zinc-800" /><p className="text-zinc-600 text-[14px]">Aucune conversation</p></div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 48px)' }}>
      <div className="flex items-center gap-3 pb-4 mb-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <button onClick={() => { setSel(null); setMessages([]); setInfo(null); clearInterval(pollRef.current); loadSessions(); }} data-testid="admin-chat-back"
          className="p-2 rounded-xl text-zinc-500 hover:text-white hover:bg-white/5"><ArrowLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[15px] font-semibold truncate">{info?.visitor_name || info?.visitor_email || `Visiteur #${sel}`}</p>
          <p className="text-zinc-600 text-[12px]">{info?.visitor_email || ''}</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="admin-chat-messages" style={{ minHeight: 0 }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[70%]">
              <div className="px-4 py-3 rounded-2xl" style={{
                backgroundColor: msg.sender === 'admin' ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.04)',
                borderBottomRightRadius: msg.sender === 'admin' ? 4 : 16,
                borderBottomLeftRadius: msg.sender === 'admin' ? 16 : 4,
              }}>
                <p className="text-zinc-200 text-[14px] leading-relaxed">{msg.message}</p>
              </div>
              <p className="text-zinc-700 text-[10px] mt-1 px-1">{msg.timestamp?.split('T')[1]?.substring(0, 5)}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-4 pt-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <input value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
          placeholder="Repondre..." className="flex-1 px-4 py-3 rounded-xl text-[14px] text-white placeholder:text-zinc-600 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }} />
        <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="admin-chat-send"
          className="px-5 py-3 rounded-xl text-black font-semibold text-[14px] transition-all disabled:opacity-30 hover:brightness-110"
          style={{ backgroundColor: GREEN }}>
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
};

// ─── USERS ───
const AdminUsers = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); fetchAdmin('/users', token).then(d => setUsers(d.users || [])).catch(() => {}).finally(() => setLoading(false)); }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Abonnes" subtitle={`${users.length} membre${users.length !== 1 ? 's' : ''}`} actions={<RefreshBtn onClick={load} />} />
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-700" size={24} /></div> : (
        <div className="space-y-2" data-testid="admin-users-list">
          {users.map((u, i) => (
            <div key={i} className="flex items-center gap-4 p-5 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.active !== false ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.04)' }}>
                {u.active !== false ? <UserCheck size={15} className="text-green-400" /> : <UserX size={15} className="text-zinc-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-[14px] font-semibold truncate">{u.email || u.phone}</p>
                <p className="text-zinc-700 text-[11px] mt-0.5">{u.created_at?.split('T')[0] || '—'}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-[11px] font-medium ${u.active !== false ? 'text-green-400' : 'text-zinc-600'}`}
                style={{ backgroundColor: u.active !== false ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)' }}>
                {u.active !== false ? 'Actif' : 'Inactif'}
              </span>
            </div>
          ))}
          {users.length === 0 && <div className="text-center py-20"><Users size={32} className="mx-auto mb-3 text-zinc-800" /><p className="text-zinc-600">Aucun membre</p></div>}
        </div>
      )}
    </div>
  );
};

// ─── PAYMENTS ───
const AdminPayments = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [counts, setCounts] = useState({ all: 0, paid: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback((status) => {
    setLoading(true);
    fetchAdmin(`/payments?limit=100&status=${status}`, token).then(d => { setPayments(d.payments); if (d.counts) setCounts(d.counts); }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(filter); }, [load, filter]);

  const st = (s) => s === 'paid' ? { c: GREEN, l: 'Paye' } : s === 'pending' ? { c: '#f59e0b', l: 'En attente' } : { c: '#ef4444', l: 'Echoue' };

  return (
    <div>
      <PageHeader title="Paiements" subtitle={`${counts.all} transaction${counts.all !== 1 ? 's' : ''}`} actions={<RefreshBtn onClick={() => load(filter)} />} />
      <div className="flex gap-2 mb-6">
        {[{ id: 'all', l: `Tous (${counts.all})` }, { id: 'paid', l: `Payes (${counts.paid})` }, { id: 'pending', l: `En attente (${counts.pending})` }, { id: 'failed', l: `Echoues (${counts.failed})` }].map(t => (
          <Pill key={t.id} active={filter === t.id} onClick={() => setFilter(t.id)}>{t.l}</Pill>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-700" size={24} /></div> : (
        <div className="space-y-2" data-testid="admin-payments-list">
          {payments.map((tx, i) => {
            const s = st(tx.payment_status);
            return (
              <div key={i} className="flex items-center gap-4 p-5 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${s.c}10` }}>
                  <CreditCard size={15} style={{ color: s.c }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[14px] font-semibold truncate">{tx.email || tx.session_id?.substring(0, 28) + '...'}</p>
                  <p className="text-zinc-700 text-[12px] mt-0.5">{tx.created_at?.split('T')[0]} - {tx.amount || 49}€</p>
                </div>
                <span className="px-3 py-1 rounded-full text-[11px] font-medium" style={{ color: s.c, backgroundColor: `${s.c}10` }}>{s.l}</span>
              </div>
            );
          })}
          {payments.length === 0 && <div className="text-center py-20"><CreditCard size={32} className="mx-auto mb-3 text-zinc-800" /><p className="text-zinc-600">Aucun paiement</p></div>}
        </div>
      )}
    </div>
  );
};

// ─── CONTENT ───
const AdminContent = () => {
  const [formateurLinks, setFormateurLinks] = useState([]);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/formateur/admin/links/all`)
      .then(r => r.json())
      .then(d => setFormateurLinks(d.formateurs || []))
      .catch(() => {});
  }, []);

  const baseUrl = 'https://gautbonac.com';

  const copyLink = (token, name) => {
    navigator.clipboard.writeText(`${baseUrl}/formateur/${token}`);
    setCopied(name);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Formations" subtitle="7 programmes disponibles" />

      {/* Formateur Links */}
      {formateurLinks.length > 0 && (
        <div>
          <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-3">Liens Formateurs</h2>
          <div className="space-y-2">
            {formateurLinks.map((f) => (
              <div key={f.token} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(0,200,255,0.03)', border: '1px solid rgba(0,200,255,0.08)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(0,200,255,0.1)' }}>
                  <ExternalLink size={13} style={{ color: '#00c8ff' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[13px] font-semibold">{f.name}</p>
                  <p className="text-zinc-600 text-[11px] truncate font-mono">{baseUrl}/formateur/{f.token}</p>
                </div>
                <span className="text-zinc-600 text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>
                  {f.module_ids?.join(', ')}
                </span>
                <button
                  data-testid={`copy-link-${f.name}`}
                  onClick={() => copyLink(f.token, f.name)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
                  style={{
                    backgroundColor: copied === f.name ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
                    color: copied === f.name ? '#22c55e' : '#a0a0b8',
                    border: `1px solid ${copied === f.name ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  }}
                >
                  {copied === f.name ? 'Copié !' : 'Copier'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {[
          { title: 'Musculation & Discipline', lessons: 20, color: '#ef4444', tag: 'Fitness' },
          { title: 'Réseaux Sociaux & E-commerce', lessons: 18, color: '#f59e0b', tag: 'Business' },
          { title: 'Création de Contenu Vidéo', lessons: 16, color: '#3b82f6', tag: 'Créatif' },
          { title: 'Montage Vidéo Pro', lessons: 17, color: '#8b5cf6', tag: 'Créatif' },
          { title: 'IA & Automatisation', lessons: 15, color: '#06b6d4', tag: 'Tech' },
          { title: 'Nutrition & Performance', lessons: 16, color: '#22c55e', tag: 'Santé' },
          { title: 'Tournage & Filming', lessons: 16, color: '#f97316', tag: 'Créatif' },
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-4 p-5 rounded-2xl transition-all hover:bg-white/[0.01]" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${f.color}12` }}>
              <BookOpen size={17} style={{ color: f.color }} />
            </div>
            <div className="flex-1">
              <p className="text-white text-[14px] font-semibold">{f.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-zinc-600 text-[11px]">{f.lessons} leçons</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-zinc-500" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}>{f.tag}</span>
              </div>
            </div>
            <ChevronRight size={15} className="text-zinc-700" />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── LEADS ───
const AdminLeads = ({ token }) => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => { setLoading(true); fetchAdmin('/leads', token).then(d => { setLeads(d.leads); setTotal(d.total); }).catch(() => {}).finally(() => setLoading(false)); }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (email) => {
    if (!window.confirm(`Supprimer ${email} ?`)) return;
    try { await fetchAdmin(`/leads/${encodeURIComponent(email)}`, token, { method: 'DELETE' }); load(); } catch {}
  };

  const handleExport = () => {
    const csv = ['Email,Langue,Pays,Ville,Date'].concat(leads.map(l => `${l.email},${l.language || ''},${l.geo?.country || ''},${l.geo?.city || ''},${l.created_at || ''}`)).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `openroom-leads-${new Date().toISOString().split('T')[0]}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const handleCopy = () => { navigator.clipboard.writeText(leads.map(l => l.email).join('\n')); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const filtered = leads.filter(l => !search || l.email?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title="Leads" subtitle={`${total} email${total !== 1 ? 's' : ''} capture${total !== 1 ? 's' : ''}`}
        actions={<>
          <button onClick={handleCopy} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium text-zinc-500 hover:text-white transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
            {copied ? <Check size={12} style={{ color: GREEN }} /> : <Copy size={12} />}{copied ? 'Copie !' : 'Copier'}
          </button>
          <button onClick={handleExport} data-testid="admin-leads-export" className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium text-zinc-500 hover:text-white transition-all" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Download size={12} />CSV
          </button>
          <RefreshBtn onClick={load} />
        </>}
      />
      <div className="relative mb-5">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input data-testid="admin-leads-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un email..."
          className="w-full pl-11 pr-4 py-3 rounded-xl text-[13px] text-white placeholder:text-zinc-600 focus:outline-none"
          style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} />
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-700" size={24} /></div> : (
        <div className="space-y-1.5" data-testid="admin-leads-list">
          {filtered.map((lead, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-2xl group transition-all hover:bg-white/[0.01]" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(6,182,212,0.06)' }}>
                <Mail size={14} className="text-cyan-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-[14px] font-semibold truncate">{lead.email}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  {lead.geo?.city && <span className="text-zinc-600 text-[11px] flex items-center gap-1"><MapPin size={9} />{lead.geo.city}{lead.geo.country ? `, ${lead.geo.country}` : ''}</span>}
                  {lead.language && <span className="text-zinc-700 text-[10px] uppercase">{lead.language}</span>}
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <p className="text-zinc-600 text-[11px]">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}</p>
                <p className="text-zinc-700 text-[10px]">{lead.created_at ? new Date(lead.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
              </div>
              <button onClick={() => handleDelete(lead.email)} data-testid={`delete-lead-${i}`}
                className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 text-zinc-700 hover:text-red-400 transition-all"><Trash2 size={14} /></button>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-20"><Mail size={32} className="mx-auto mb-3 text-zinc-800" /><p className="text-zinc-600">{search ? 'Aucun resultat' : 'Aucun lead'}</p></div>}
        </div>
      )}
    </div>
  );
};

// ─── NOTIFICATION BELL ───
const NotificationBell = ({ token, onOpenChat }) => {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const prevCountRef = useRef(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    fetch(`${CHAT_API}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => {
        const newNotifs = d.notifications || [];
        if (newNotifs.length > prevCountRef.current && prevCountRef.current >= 0) {
          playNotifSound();
        }
        prevCountRef.current = newNotifs.length;
        setNotifs(newNotifs);
      }).catch(() => {});
  }, [token]);

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = () => {
    fetch(`${CHAT_API}/notifications/mark-read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(() => setNotifs([])).catch(() => {});
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="admin-notification-bell" onClick={() => { setOpen(!open); if (!open && notifs.length > 0) markRead(); }}
        className="relative p-2.5 rounded-xl transition-all hover:bg-white/5">
        {notifs.length > 0 ? <BellRing size={18} style={{ color: GREEN }} className="animate-pulse" /> : <Bell size={18} className="text-zinc-600" />}
        {notifs.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-black" style={{ backgroundColor: GREEN }}>
            {notifs.length}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="admin-notification-dropdown" className="absolute right-0 top-12 w-[320px] rounded-2xl overflow-hidden z-50"
          style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="text-white text-[13px] font-semibold">Notifications</span>
            {notifs.length > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ color: GREEN, backgroundColor: 'rgba(34,197,94,0.1)' }}>{notifs.length} new</span>}
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="py-10 text-center"><Bell size={24} className="mx-auto mb-2 text-zinc-800" /><p className="text-zinc-600 text-[13px]">Aucune notification</p></div>
            ) : notifs.map((n, i) => (
              <button key={i} data-testid={`notification-item-${i}`} onClick={() => { setOpen(false); onOpenChat(n.session_id); }}
                className="w-full text-left px-4 py-3 transition-all hover:bg-white/[0.03]"
                style={{ borderBottom: i < notifs.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                    <MessageSquare size={14} style={{ color: GREEN }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-[13px] font-semibold truncate">{n.visitor_name || 'Visiteur'}</p>
                    <p className="text-zinc-500 text-[12px] truncate mt-0.5">{n.message}</p>
                    <p className="text-zinc-700 text-[10px] mt-1">{n.created_at ? new Date(n.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PUSH PERMISSION BANNER ───
const PushPermissionBanner = ({ token }) => {
  const [show, setShow] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    if (Notification.permission === 'default') setShow(true);
  }, []);

  const enable = async () => {
    setEnabling(true);
    const perm = await Notification.requestPermission();
    if (perm === 'granted') {
      await registerPushSubscription(token);
      setShow(false);
    }
    setEnabling(false);
  };

  if (!show) return null;

  return (
    <div data-testid="push-permission-banner" className="mx-5 mt-4 mb-0 px-4 py-3 rounded-xl flex items-center gap-3"
      style={{ backgroundColor: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
      <BellRing size={18} style={{ color: GREEN }} className="flex-shrink-0" />
      <div className="flex-1">
        <p className="text-white text-[13px] font-semibold">Activer les notifications</p>
        <p className="text-zinc-500 text-[11px] mt-0.5">Recois une alerte quand un visiteur t'ecrit</p>
      </div>
      <button data-testid="enable-push-btn" onClick={enable} disabled={enabling}
        className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-black transition-all hover:brightness-110 disabled:opacity-50"
        style={{ backgroundColor: GREEN }}>
        {enabling ? 'Activation...' : 'Activer'}
      </button>
      <button onClick={() => setShow(false)} className="p-1 text-zinc-600 hover:text-zinc-400"><X size={14} /></button>
    </div>
  );
};


// ─── BROADCAST EMAILS ───
const AdminBroadcast = ({ token }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [topic, setTopic] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  const fetchEmails = useCallback(async () => {
    try {
      const res = await fetch(`${BROADCAST_API}/emails`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${BROADCAST_API}/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: topic || null })
      });
      if (res.ok) { setTopic(''); await fetchEmails(); }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const handleSend = async (emailId) => {
    if (!window.confirm('Envoyer cet email à TOUTE la liste ?')) return;
    setSending(emailId);
    try {
      const res = await fetch(`${BROADCAST_API}/send/${emailId}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) { alert(`Email envoyé à ${data.recipients_count} personnes !`); await fetchEmails(); }
      else alert(data.detail || 'Erreur');
    } catch (e) { console.error(e); alert('Erreur réseau'); }
    setSending(null);
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      await fetch(`${BROADCAST_API}/emails/${emailId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      await fetchEmails();
    } catch (e) { console.error(e); }
  };

  const handleCustomSave = async () => {
    if (!customSubject.trim() || !customBody.trim()) return;
    setSavingCustom(true);
    try {
      const res = await fetch(`${BROADCAST_API}/custom`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subject: customSubject, body: customBody })
      });
      if (res.ok) { setShowCustom(false); setCustomSubject(''); setCustomBody(''); await fetchEmails(); }
    } catch (e) { console.error(e); }
    setSavingCustom(false);
  };

  const drafts = emails.filter(e => e.status === 'draft');
  const sent = emails.filter(e => e.status === 'sent');

  return (
    <div className="space-y-6" data-testid="admin-broadcast">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-white text-xl font-bold">Emails Broadcast</h1>
          <p className="text-zinc-500 text-sm mt-1">Génère et envoie des emails motivants à toute ta liste</p>
        </div>
        <button onClick={fetchEmails} className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-all"><RefreshCw size={16} /></button>
      </div>

      {/* AI Generate */}
      <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(0,200,255,0.04)', border: '1px solid rgba(0,200,255,0.1)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-cyan-400" />
          <span className="text-white text-sm font-semibold">Générer avec l'IA</span>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="broadcast-topic-input"
            value={topic} onChange={e => setTopic(e.target.value)}
            placeholder="Thème (optionnel) : discipline, hustle, mindset..."
            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-cyan-500/30"
          />
          <button
            data-testid="broadcast-generate-btn"
            onClick={handleGenerate} disabled={generating}
            className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#00c8ff,#0090dd)' }}>
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating ? 'Génération...' : 'Générer'}
          </button>
        </div>
        <p className="text-zinc-600 text-xs mt-2">L'IA génère un email style Tate + preview envoyé sur Telegram</p>
      </div>

      {/* Custom Email */}
      {!showCustom ? (
        <button
          data-testid="broadcast-custom-toggle"
          onClick={() => setShowCustom(true)}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm transition-all">
          <PenLine size={14} /> Écrire un email custom
        </button>
      ) : (
        <div className="rounded-xl p-5" style={{ backgroundColor: 'rgba(180,60,255,0.04)', border: '1px solid rgba(180,60,255,0.1)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <PenLine size={16} className="text-purple-400" />
              <span className="text-white text-sm font-semibold">Email custom</span>
            </div>
            <button onClick={() => setShowCustom(false)} className="text-zinc-600 hover:text-zinc-400"><X size={14} /></button>
          </div>
          <input
            data-testid="broadcast-custom-subject"
            value={customSubject} onChange={e => setCustomSubject(e.target.value)}
            placeholder="Sujet de l'email"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/30 mb-2"
          />
          <textarea
            data-testid="broadcast-custom-body"
            value={customBody} onChange={e => setCustomBody(e.target.value)}
            placeholder="Contenu de l'email (une phrase par ligne, style direct)"
            rows={6}
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/30 resize-none"
          />
          <div className="flex justify-end mt-3">
            <button
              data-testid="broadcast-custom-save"
              onClick={handleCustomSave} disabled={savingCustom || !customSubject.trim() || !customBody.trim()}
              className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg,#b43cff,#8a2be2)' }}>
              {savingCustom ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Sauvegarder en brouillon
            </button>
          </div>
        </div>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <div>
          <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-3">Brouillons ({drafts.length})</h2>
          <div className="space-y-2">
            {drafts.map(email => (
              <div key={email.email_id} className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{email.subject}</p>
                    <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{email.body?.substring(0, 120)}...</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-zinc-600 text-[11px] flex items-center gap-1">
                        <Clock size={10} /> {new Date(email.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">{email.source === 'ai' ? 'IA' : 'Custom'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid={`broadcast-preview-${email.email_id}`}
                      onClick={() => setPreviewId(previewId === email.email_id ? null : email.email_id)}
                      className="p-2 rounded-lg text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all" title="Aperçu">
                      <Eye size={14} />
                    </button>
                    <button
                      data-testid={`broadcast-send-${email.email_id}`}
                      onClick={() => handleSend(email.email_id)} disabled={sending === email.email_id}
                      className="p-2 rounded-lg text-zinc-500 hover:text-green-400 hover:bg-green-500/5 transition-all" title="Envoyer">
                      {sending === email.email_id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                    <button
                      data-testid={`broadcast-delete-${email.email_id}`}
                      onClick={() => handleDelete(email.email_id)}
                      className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/5 transition-all" title="Supprimer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {previewId === email.email_id && (
                  <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                    <iframe
                      src={`${BROADCAST_API}/preview/${email.email_id}`}
                      title="Email Preview"
                      className="w-full border-0"
                      style={{ height: '500px', background: '#04021a' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent */}
      {sent.length > 0 && (
        <div>
          <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-wider mb-3">Envoyés ({sent.length})</h2>
          <div className="space-y-2">
            {sent.map(email => (
              <div key={email.email_id} className="rounded-xl p-4" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{email.subject}</p>
                    <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{email.body?.substring(0, 120)}...</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-zinc-600 text-[11px] flex items-center gap-1">
                        <Send size={10} /> {new Date(email.sent_at).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">{email.recipients_count} destinataires</span>
                      {email.errors_count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">{email.errors_count} erreurs</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewId(previewId === email.email_id ? null : email.email_id)}
                    className="p-2 rounded-lg text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all" title="Aperçu">
                    <Eye size={14} />
                  </button>
                </div>
                {previewId === email.email_id && (
                  <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                    <iframe
                      src={`${BROADCAST_API}/preview/${email.email_id}`}
                      title="Email Preview"
                      className="w-full border-0"
                      style={{ height: '500px', background: '#04021a' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-center py-8"><Loader2 size={20} className="animate-spin text-zinc-500 mx-auto" /></div>}
      {!loading && emails.length === 0 && (
        <div className="text-center py-12">
          <Megaphone size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Aucun email broadcast pour le moment</p>
          <p className="text-zinc-600 text-xs mt-1">Génère ton premier email avec l'IA ci-dessus</p>
        </div>
      )}
    </div>
  );
};


// ─── SIDEBAR ───
const AdminSidebar = ({ activeTab, setActiveTab, onLogout, unreadChats, open, setOpen }) => {
  const sections = [
    { items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
    { label: 'Analytique', items: [
      { id: 'visitors', label: 'Visiteurs', icon: BarChart3 },
      { id: 'leads', label: 'Leads', icon: Mail },
    ]},
    { label: 'Gestion', items: [
      { id: 'chat', label: 'Messages', icon: MessageSquare, badge: unreadChats },
      { id: 'broadcast', label: 'Broadcast', icon: Megaphone },
      { id: 'users', label: 'Abonnés', icon: Users },
      { id: 'payments', label: 'Paiements', icon: CreditCard },
      { id: 'content', label: 'Formations', icon: BookOpen },
    ]},
  ];

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed lg:static z-50 top-0 left-0 h-full w-[240px] flex-shrink-0 flex flex-col transition-transform lg:transform-none ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="admin-sidebar" style={{ backgroundColor: '#0c0c0c', borderRight: '1px solid rgba(255,255,255,0.05)' }}>

        <div className="px-6 pt-7 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GREEN }} />
            <span className="text-white text-[15px] font-bold tracking-tight" style={{ fontFamily: '"Inter", sans-serif' }}>OpenRoom</span>
          </div>
          <button className="lg:hidden p-1 text-zinc-600" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>

        <nav className="flex-1 px-3 pt-2 space-y-6 overflow-y-auto">
          {sections.map((section, si) => (
            <div key={si}>
              {section.label && <p className="text-zinc-700 text-[10px] font-semibold uppercase tracking-[0.15em] px-3 mb-2">{section.label}</p>}
              <div className="space-y-0.5">
                {section.items.map(tab => {
                  const Icon = tab.icon;
                  const active = activeTab === tab.id;
                  return (
                    <button key={tab.id} data-testid={`admin-tab-${tab.id}`}
                      onClick={() => { setActiveTab(tab.id); setOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all text-[13px] ${active ? 'text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.02]'}`}
                      style={{ backgroundColor: active ? 'rgba(34,197,94,0.08)' : 'transparent', fontFamily: '"Inter", sans-serif' }}>
                      <Icon size={16} style={{ color: active ? GREEN : undefined }} />
                      <span className="flex-1 font-medium">{tab.label}</span>
                      {tab.badge > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ backgroundColor: GREEN }}>{tab.badge}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 pb-6 space-y-1">
          <div className="h-px mx-3 mb-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }} />
          <a href="/" data-testid="admin-back-site" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.02] transition-all text-[13px] no-underline"><ExternalLink size={14} />Voir le site</a>
          <button data-testid="admin-logout" onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-600 hover:text-red-400 hover:bg-red-500/[0.04] transition-all text-[13px]"><LogOut size={14} />Deconnexion</button>
        </div>
      </aside>
    </>
  );
};

// ─── MAIN ───
const AdminPanel = () => {
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [unreadChats, setUnreadChats] = useState(0);
  const [autoLogging, setAutoLogging] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('key')) {
      // Always re-authenticate when key is present (handles expired tokens)
      setAutoLogging(true);
      fetch(`${API}/login-bypass`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key: params.get('key') }) })
        .then(r => { if (!r.ok) throw new Error('fail'); return r.json(); })
        .then(d => { if (d.access_token) { localStorage.setItem('admin_token', d.access_token); setToken(d.access_token); window.history.replaceState({}, '', '/admin'); } })
        .catch(() => { localStorage.removeItem('admin_token'); setToken(''); })
        .finally(() => setAutoLogging(false));
    }
  }, []);

  // Register push subscription after login
  useEffect(() => {
    if (!token) return;
    if ('Notification' in window && Notification.permission === 'granted') {
      registerPushSubscription(token);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const check = () => fetchAdmin('/chat/sessions', token).then(d => setUnreadChats(d.sessions.filter(s => s.unread).length)).catch(() => {});
    check(); const i = setInterval(check, 15000); return () => clearInterval(i);
  }, [token]);

  const handleOpenChatFromNotif = (sessionId) => {
    setActiveTab('chat');
  };

  if (autoLogging) return <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#080808' }}><Loader2 className="animate-spin" size={24} style={{ color: GREEN }} /></div>;
  if (!token) return <AdminLoginForm onLogin={setToken} />;

  const pages = { leads: AdminLeads, visitors: AdminVisitors, chat: AdminChat, users: AdminUsers, payments: AdminPayments, content: AdminContent, dashboard: AdminDashboard, broadcast: AdminBroadcast };
  const Page = pages[activeTab] || AdminDashboard;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#080808' }}>
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={() => { setToken(null); localStorage.removeItem('admin_token'); }} unreadChats={unreadChats} open={sidebarOpen} setOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="flex items-center justify-between px-4 lg:px-8 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-xl text-zinc-500 hover:text-white"><Menu size={20} /></button>
            <div className="flex items-center gap-2 lg:hidden"><div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GREEN }} /><span className="text-white text-[14px] font-bold">OpenRoom</span></div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell token={token} onOpenChat={handleOpenChatFromNotif} />
          </div>
        </header>
        <PushPermissionBanner token={token} />
        <main className="flex-1 p-5 lg:p-8 xl:p-10 overflow-auto">
          <Page token={token} />
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
